import { Badge, Button, Select, Text } from '@cloudflare/kumo'
import { MagicWand, X } from '@phosphor-icons/react'
import {
  TAXONOMY,
  classifyFactoryTaxonomy,
  type FactoryInput,
  type FactoryTaxonomyKey,
} from '@repo/shared'
import { useState } from 'react'
import { factoryTaxonomyText } from '../lib/factory-filters'

/** 공장 분류는 식품·메뉴 축만 고른다. 코스·테이스팅은 식당 코스의 자리라 공장 품목이 아니다 (factory-taxonomy.ts) */
const FACTORY_AXES = ['product', 'menu'] as const
type FactoryAxis = (typeof FACTORY_AXES)[number]
const AXIS_LABEL: Record<FactoryAxis, string> = { product: '식품', menu: '메뉴' }
const CONFIDENCE_LABEL = {
  high: '자동 · 높음',
  medium: '자동 · 보통',
  low: '자동 · 낮음',
  manual: '관리자',
}

/**
 * 공장 분류 고치기 (대표 결정 10/30). 레시피캣 taxonomy 의 대(·중)분류를 붙이고 뗀다. 고치면 신뢰도가 '관리자'가 되고
 * '분류 확인 필요'가 풀린다. '자동 분류'는 품목·메모 글자로 classifyFactoryTaxonomy 를 다시 돌린다 (시드 도구와 같은 규칙)
 */
export function FactoryTaxonomyEditor({
  form,
  setForm,
}: {
  form: FactoryInput
  setForm: (f: FactoryInput) => void
}) {
  const [axis, setAxis] = useState<FactoryAxis>('product')
  const [majorId, setMajorId] = useState(TAXONOMY.product[0]!.id)
  const [subId, setSubId] = useState('')
  const major = TAXONOMY[axis].find((m) => m.id === majorId) ?? TAXONOMY[axis][0]!
  const manual = (keys: FactoryTaxonomyKey[]) =>
    setForm({
      ...form,
      taxonomyKeys: keys,
      taxonomyConfidence: 'manual',
      taxonomyNeedsReview: false,
    })
  const add = () => {
    const key = { axis, majorId: major.id, subId: subId || null }
    if (
      form.taxonomyKeys.some(
        (k) => k.axis === key.axis && k.majorId === key.majorId && k.subId === key.subId,
      )
    )
      return
    manual([...form.taxonomyKeys, key].slice(0, 12))
  }
  const auto = () => {
    const guess = classifyFactoryTaxonomy(`${form.items} ${form.notes}`)
    setForm({
      ...form,
      taxonomyKeys: guess.keys,
      taxonomyConfidence: guess.confidence,
      taxonomyNeedsReview: guess.needsReview,
      categoryIds: [...new Set([...form.categoryIds, ...guess.categoryIds])],
    })
  }

  return (
    <fieldset className="flex flex-col gap-2" data-testid="factory-taxonomy">
      <legend className="text-sm font-medium">
        분류 (식품·메뉴){' '}
        {form.taxonomyConfidence && (
          <Badge variant="outline">{CONFIDENCE_LABEL[form.taxonomyConfidence]}</Badge>
        )}{' '}
        {form.taxonomyNeedsReview && <Badge variant="warning">분류 확인 필요</Badge>}
      </legend>
      <div className="flex flex-wrap gap-2">
        {form.taxonomyKeys.length === 0 && (
          <Text variant="secondary" size="sm">
            아직 분류가 없어요.
          </Text>
        )}
        {form.taxonomyKeys.map((k, i) => (
          <span
            key={`${k.axis}:${k.majorId}:${k.subId}`}
            className="flex items-center gap-1 text-sm"
          >
            {factoryTaxonomyText([k])}
            {k.subId &&
              ` · ${TAXONOMY[k.axis].find((m) => m.id === k.majorId)?.subs.find((s) => s.id === k.subId)?.ko ?? k.subId}`}
            <Button
              variant="ghost"
              shape="square"
              size="sm"
              icon={<X />}
              aria-label="분류 빼기"
              onClick={() => manual(form.taxonomyKeys.filter((_, j) => j !== i))}
            />
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <Select
          label="축"
          value={axis}
          onValueChange={(v: string | null) => {
            if (!v) return
            setAxis(v as FactoryAxis)
            setMajorId(TAXONOMY[v as FactoryAxis][0]!.id)
            setSubId('')
          }}
          items={FACTORY_AXES.map((a) => ({ label: AXIS_LABEL[a], value: a }))}
        />
        <Select
          label="대분류"
          value={major.id}
          onValueChange={(v: string | null) => {
            if (!v) return
            setMajorId(v)
            setSubId('')
          }}
          items={TAXONOMY[axis].map((m) => ({ label: m.ko, value: m.id }))}
        />
        <Select
          label="세부 (선택)"
          value={subId}
          onValueChange={(v: string | null) => setSubId(v ?? '')}
          items={[
            { label: '대분류 전체', value: '' },
            ...major.subs.map((s) => ({ label: s.ko, value: s.id })),
          ]}
        />
        <Button variant="secondary" onClick={add}>
          분류 붙이기
        </Button>
        <Button variant="ghost" icon={<MagicWand />} onClick={auto}>
          품목 글자로 자동 분류
        </Button>
      </div>
    </fieldset>
  )
}
